// "Volver de generar y encontrar lo que generaste", para los modos con RUTA PROPIA
// (Animar imagen, Carrusel, los atajos de Ediciones rápidas) —
// ADR-2026-09-10-los-modos-con-ruta-propia-tambien-vuelven-a-lo-que-generaste.
//
// En mobile estos modos se llevan la pantalla: loader a pantalla completa y después
// `/redes-sociales/imagenes-generadas`. El "Atrás" de ahí los remonta de cero, así que el
// usuario volvía a la primera pantalla del modo —a elegir la imagen otra vez— en vez de a
// lo que acababa de generar.
//
// ── Por qué NO usa el canal de `restoreV2` ──────────────────────────────────────
//
// `InterfazV2` guarda su configuración en `sessionStorage` porque su modo no es una ruta:
// abrir el modo y volver a él son el mismo montaje de la misma pantalla, y el blob es lo
// único que sobrevive. Estos modos SÍ son una ruta, así que su configuración puede viajar
// en la ENTRADA DE HISTORIAL, que es donde el "Atrás" ya sabe volver:
//
//   1. antes de irse, el modo REEMPLAZA su propia entrada con una que lleva la
//      configuración (`replace`, no `push`: no agrega un paso al historial);
//   2. navega a los resultados;
//   3. el "Atrás" —el botón o el del navegador— hace POP a esa entrada, el modo monta y
//      lee la configuración de `history.state`.
//
// La diferencia que importa: **no queda nada que limpiar**. Un blob en `sessionStorage`
// sobrevive al "Atrás" y se lo come el próximo montaje aunque el usuario haya ido a otro
// lado — que es exactamente el bug que "Listo" y "Seguir generando" tienen que barrer a
// mano (ADR-2026-09-10-seguir-generando-va-al-inicio). Una entrada de historial vale sólo
// para SU entrada: si el usuario se va al inicio, ahí no hay ninguna configuración.

import type { NavigateFunction } from 'react-router-dom';

/** La clave dentro de `history.state.usr`. Un solo lugar: la escribe y la lee este módulo. */
const CLAVE = 'configDelModo';

/**
 * Deja la configuración actual en la entrada de historial del modo, para que el "Atrás"
 * la devuelva. Se llama JUSTO ANTES de irse a los resultados.
 *
 * `replace` y no `push`: es la misma pantalla en la que el usuario ya está: agregar una
 * entrada le pondría un paso fantasma al botón de atrás.
 */
export function recordarConfigDelModo<T>(
  navigate: NavigateFunction,
  ruta: string,
  config: T,
): void {
  // El estado que la entrada ya tenía se CONSERVA: en las rutas de `/interfaz-v2/modo/*`
  // ahí viaja lo que abrió el modo (`presetData`, `imagenBase`…), y pisarlo dejaría al
  // "Atrás" con la configuración puesta pero sin el modo que la usa.
  let previo: Record<string, unknown> = {};
  try {
    previo =
      ((window.history.state as { usr?: Record<string, unknown> } | null)?.usr as
        | Record<string, unknown>
        | undefined) ?? {};
  } catch {
    /* noop */
  }
  navigate(ruta, { replace: true, state: { ...previo, [CLAVE]: config } });
}

/**
 * La configuración con la que se volvió, o `null` si es una entrada limpia.
 *
 * Se lee de `window.history.state` y no con `useLocation` para poder usarla en el
 * initializer de un `useState`: el modo tiene que montar YA con lo suyo puesto, no
 * aplicarlo en un efecto un frame después (eso es un parpadeo de la primera pantalla). Es
 * el mismo mecanismo con el que `InterfazV2` lee su preset de entrada.
 */
export function configDelModoAlMontar<T>(): T | null {
  try {
    const usr = (window.history.state as { usr?: Record<string, unknown> } | null)?.usr;
    const config = usr?.[CLAVE];
    return config ? (config as T) : null;
  } catch {
    return null;
  }
}
